/**
 * Reviews panel for an expediente: lists scheduled periodic re-reviews, lets the
 * analyst schedule a new one, and update the status of an existing one. Talks to
 * the real backend via `api.listReviews` / `api.createReview` / `api.updateReview`
 * with TanStack Query. Presentational style matches the compliance design system
 * (Card / Pill / Button). es-PA copy only.
 */
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { formatDate } from '../lib/hooks';
import { useToast } from '../lib/use-toast';
import {
  CADENCIA,
  parseReviewDate,
  renewalStatus,
  type CreateReviewInput,
  type NivelRiesgo,
  type Review,
  type ReviewStatus,
  type ReviewType,
} from '../lib/phase1';
import { Button, Card, EmptyState, Label, Pill, Skeleton, type Tone } from './ui';
import { CalendarClock, CalendarPlus, Plus, RefreshCw, RotateCcw } from 'lucide-react';

const TYPE_LABEL: Record<ReviewType, string> = {
  periodica: 'Periódica',
  por_riesgo: 'Por riesgo',
  evento: 'Por evento',
};

const STATUS_LABEL: Record<ReviewStatus, string> = {
  pendiente: 'Pendiente',
  en_proceso: 'En proceso',
  completada: 'Completada',
  omitida: 'Omitida',
};

const STATUS_TONE: Record<ReviewStatus, Tone> = {
  pendiente: 'amber',
  en_proceso: 'blue',
  completada: 'green',
  omitida: 'slate',
};

const TYPE_OPTIONS: ReviewType[] = ['periodica', 'por_riesgo', 'evento'];
const STATUS_OPTIONS: ReviewStatus[] = ['pendiente', 'en_proceso', 'completada', 'omitida'];

const SELECT_CLS =
  'h-10 w-full rounded-lg border border-line-strong bg-canvas px-3 text-sm text-ink focus:border-primary focus:bg-panel focus:outline-none focus:ring-2 focus:ring-primary/30';

/** Today (local) as a yyyy-mm-dd string for the date input default/min. */
function todayInput(): string {
  const now = new Date();
  const off = now.getTimezoneOffset();
  return new Date(now.getTime() - off * 60_000).toISOString().slice(0, 10);
}

interface NewReviewFormProps {
  busy: boolean;
  onCancel: () => void;
  onSubmit: (input: CreateReviewInput) => void;
}

function NewReviewForm({ busy, onCancel, onSubmit }: NewReviewFormProps) {
  const [scheduledFor, setScheduledFor] = useState(todayInput());
  const [reviewType, setReviewType] = useState<ReviewType>('periodica');

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (!scheduledFor) return;
        // The backend's createReviewSchema expects a date-only string (YYYY-MM-DD);
        // the date input already provides exactly that, so send it as-is.
        onSubmit({ scheduledFor, reviewType });
      }}
      className="mt-4 grid gap-4 rounded-2xl border border-line bg-canvas p-4"
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <Label htmlFor="review-date">Fecha programada</Label>
          <input
            id="review-date"
            type="date"
            required
            min={todayInput()}
            value={scheduledFor}
            onChange={(e) => setScheduledFor(e.target.value)}
            className={SELECT_CLS}
          />
        </div>
        <div>
          <Label htmlFor="review-type">Tipo de revisión</Label>
          <select
            id="review-type"
            value={reviewType}
            onChange={(e) => setReviewType(e.target.value as ReviewType)}
            className={SELECT_CLS}
          >
            {TYPE_OPTIONS.map((t) => (
              <option key={t} value={t}>
                {TYPE_LABEL[t]}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="flex justify-end gap-3">
        <Button type="button" variant="outline" onClick={onCancel} disabled={busy}>
          Cancelar
        </Button>
        <Button type="submit" disabled={busy || !scheduledFor}>
          {busy ? 'Programando…' : 'Programar'}
        </Button>
      </div>
    </form>
  );
}

interface ReviewRowProps {
  review: Review;
  busy: boolean;
  onStatusChange: (status: ReviewStatus) => void;
}

function ReviewRow({ review, busy, onStatusChange }: ReviewRowProps) {
  return (
    <div className="flex flex-wrap items-center gap-3.5 rounded-2xl border border-line bg-canvas px-4 py-3.5">
      <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-panel text-ink-soft">
        <CalendarClock className="h-5 w-5" strokeWidth={1.75} />
      </span>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-bold text-ink">{formatDate(review.scheduledFor, 'Sin fecha')}</div>
        <div className="truncate font-mono text-[10.5px] text-ink-faint">
          {TYPE_LABEL[review.reviewType]}
          {review.cadenceMonths ? ` · cada ${review.cadenceMonths} meses` : ''}
          {review.assignedTo ? ` · ${review.assignedTo}` : ''}
        </div>
      </div>
      <Pill tone={STATUS_TONE[review.status]} className="shrink-0">
        {STATUS_LABEL[review.status]}
      </Pill>
      <select
        aria-label="Cambiar estado de la revisión"
        value={review.status}
        disabled={busy}
        onChange={(e) => onStatusChange(e.target.value as ReviewStatus)}
        className="h-9 shrink-0 rounded-lg border border-line-strong bg-panel px-2.5 text-xs font-semibold text-ink focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30 disabled:opacity-50"
      >
        {STATUS_OPTIONS.map((s) => (
          <option key={s} value={s}>
            {STATUS_LABEL[s]}
          </option>
        ))}
      </select>
    </div>
  );
}

/**
 * §3 — renovación de debida diligencia. Shows the expediente's next-review date
 * and the cadence for its risk nivel (bajo 36 / medio 24 / alto 12 meses,
 * derived from shared), with a badge when the renewal is overdue ("vencida") or
 * coming due within 30 days ("próxima"). Skipped for an unevaluated expediente.
 */
function RenewalBanner({ riesgo, review }: { riesgo: NivelRiesgo; review: string | null }) {
  if (riesgo === 'sin') return null;
  const nextReview = parseReviewDate(review);
  const status = renewalStatus(review);
  const tone: Tone = status === 'vencida' ? 'red' : status === 'proxima' ? 'amber' : 'green';
  const badge =
    status === 'vencida'
      ? 'Renovación vencida'
      : status === 'proxima'
        ? 'Renovación próxima'
        : 'Al día';

  return (
    <div className="mb-4 rounded-2xl border border-line bg-canvas px-4 py-3.5">
      <div className="flex flex-wrap items-center gap-3">
        <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-panel text-ink-soft">
          <RefreshCw className="h-5 w-5" strokeWidth={1.75} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-bold text-ink">Renovación de debida diligencia</div>
          <div className="text-[11.5px] text-ink-soft">
            Cadencia por riesgo {RIESGO_LABEL_LOWER[riesgo]}: cada <b>{CADENCIA[riesgo]}</b>
            {nextReview ? (
              <>
                {' · '}próxima revisión <b>{formatReviewDate(nextReview)}</b>
              </>
            ) : (
              <> · próxima revisión sin programar</>
            )}
          </div>
        </div>
        <Pill tone={tone} className="shrink-0">
          {badge}
        </Pill>
      </div>
    </div>
  );
}

const RIESGO_LABEL_LOWER: Record<Exclude<NivelRiesgo, 'sin'>, string> = {
  bajo: 'bajo',
  medio: 'medio',
  alto: 'alto',
};

/** es-PA long date from a YYYY-MM-DD (UTC, no time). */
function formatReviewDate(iso: string): string {
  const d = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return iso;
  return new Intl.DateTimeFormat('es-PA', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(d);
}

export default function ReviewsPanel({
  expedienteId,
  riesgo,
  review,
}: {
  expedienteId: string;
  riesgo: NivelRiesgo;
  review: string | null;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const [adding, setAdding] = useState(false);

  const reviewsKey = ['expedientes', expedienteId, 'reviews'] as const;

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: reviewsKey,
    queryFn: () => api.listReviews(expedienteId),
    enabled: !!expedienteId,
  });

  const create = useMutation({
    mutationFn: (input: CreateReviewInput) => api.createReview(expedienteId, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: reviewsKey });
      setAdding(false);
      toast({ message: 'Revisión programada', kind: 'success' });
    },
    onError: () => toast({ message: 'No se pudo programar la revisión', kind: 'error' }),
  });

  const update = useMutation({
    mutationFn: (vars: { id: string; status: ReviewStatus }) =>
      api.updateReview(expedienteId, vars.id, { status: vars.status }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: reviewsKey });
      toast({ message: 'Estado actualizado', kind: 'success' });
    },
    onError: () => toast({ message: 'No se pudo actualizar la revisión', kind: 'error' }),
  });

  const reviews = data ?? [];
  const busy = create.isPending || update.isPending;

  return (
    <Card>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-serif text-xl font-medium text-ink">Revisiones programadas</h2>
          <p className="mt-0.5 text-sm text-ink-soft">
            Re-evaluaciones periódicas del expediente según su riesgo.
          </p>
        </div>
        {!adding && (
          <Button variant="outline" onClick={() => setAdding(true)} disabled={isLoading || busy}>
            <Plus className="h-4 w-4" strokeWidth={2.5} /> Programar
          </Button>
        )}
      </div>

      <RenewalBanner riesgo={riesgo} review={review} />

      {adding && (
        <NewReviewForm
          busy={create.isPending}
          onCancel={() => setAdding(false)}
          onSubmit={(input) => create.mutate(input)}
        />
      )}

      <div className="mt-4">
        {isLoading ? (
          <div className="grid gap-2.5">
            <Skeleton className="h-[68px] rounded-2xl" />
            <Skeleton className="h-[68px] rounded-2xl" />
            <Skeleton className="h-[68px] rounded-2xl" />
          </div>
        ) : isError ? (
          <EmptyState
            icon={<CalendarClock className="h-8 w-8" strokeWidth={1.5} />}
            title="No se pudieron cargar las revisiones"
            hint="Revisa tu conexión e inténtalo de nuevo."
            action={
              <Button variant="outline" onClick={() => void refetch()}>
                <RotateCcw className="h-4 w-4" strokeWidth={2} /> Reintentar
              </Button>
            }
          />
        ) : reviews.length === 0 ? (
          <EmptyState
            icon={<CalendarPlus className="h-8 w-8" strokeWidth={1.5} />}
            title="Sin revisiones programadas"
            hint="Programa una revisión periódica para mantener el expediente al día."
            action={
              !adding ? (
                <Button onClick={() => setAdding(true)} disabled={busy}>
                  <Plus className="h-4 w-4" strokeWidth={2.5} /> Programar revisión
                </Button>
              ) : undefined
            }
          />
        ) : (
          <div className="grid gap-2.5 [&>*]:min-w-0">
            {reviews.map((r) => (
              <ReviewRow
                key={r.id}
                review={r}
                busy={busy}
                onStatusChange={(status) => {
                  if (status !== r.status) update.mutate({ id: r.id, status });
                }}
              />
            ))}
          </div>
        )}
      </div>
    </Card>
  );
}
